import App from "../../../../vendor_core/core/src/App";
import ViewManager from "../../../../vendor_core/core/src/utils/ViewManager";
import { DefaultLayers } from "../../../../vendor_core/core/src/utils/Layers";
import ConsoleUtil from "../../../../vendor_core/core/src/utils/ConsoleUtil";
import { RenderTexture, Sprite } from "pixi.js";
import BURJackpotsModel from "../../models/BURJackpotsModel";
import { isMystery } from "./BURHistoryConstants";

// Symbol names shown in the history tables, by BUR symbol id.
// Coins and jackpot fragments are not listed: they float over a cell and never replace its symbol in the grids.
const SYMBOL_NAMES: { [id: number]: string } = {
    1: "WR",
    2: "H1",
    3: "H2",
    4: "M3",
    5: "M4",
    6: "M5",
    7: "M6",
    8: "A",
    9: "K",
    10: "Q",
    11: "J",
    20: "MYSTERY",
    21: "MWILD"
};

// The static images come from the History~ folder of the layouts, packed into the main spritesheets:
// History~%/WR ... History~%/Q for the symbols, History~%/mini_text ... History~%/mega_text for the jackpot tiers.
// There is one mystery image (MYST), used for both mystery symbols.
const HISTORY_TEXTURES = "History~%";
const MYSTERY_TEXTURE = "MYST";

// URLs of the rendered textures, for the session: blob URLs when they were made by the warm-up, data URLs when
// they were needed before it got to them
const images = new Map<string, string | null>();

export function getSymbolName(id: number): string {
    return SYMBOL_NAMES[id] || `${id}`;
}

// Symbols are centred at their own size in a frame the size of a reel cell, so they keep their relative sizes
export function getSymbolImage(id: number): string | null {
    const name = isMystery(id) ? MYSTERY_TEXTURE : SYMBOL_NAMES[id];
    return name ? getTextureImage(`${HISTORY_TEXTURES}/${name}`, true) : null;
}

// Jackpot tiers are shown as their text (mini, minor, medium, major, mega), at the size of the image
export function getJackpotImage(tier: string): string | null {
    return getTextureImage(
        `${HISTORY_TEXTURES}/${tier.toLowerCase()}_text`,
        false
    );
}

/**
 * Renders the images ahead of time, so opening the history doesn't have to (the PNG encoding of the first ones is
 * noticeable). They are encoded to blob URLs, which unlike the data URLs are encoded off the main thread and are
 * short to set on an image. Meant to be run while the game is loading. Resolves when all are done.
 */
export function warmUpHistoryImages(): Promise<void> {
    const jobs: { textureId: string; inReelCell: boolean }[] = [
        ...Array.from(
            new Set(
                Object.keys(SYMBOL_NAMES).map((id) =>
                    isMystery(Number(id))
                        ? MYSTERY_TEXTURE
                        : SYMBOL_NAMES[Number(id)]
                )
            )
        ).map((name) => ({
            textureId: `${HISTORY_TEXTURES}/${name}`,
            inReelCell: true
        })),
        ...BURJackpotsModel.jackpot_tiers.map((tier) => ({
            textureId: `${HISTORY_TEXTURES}/${tier.toLowerCase()}_text`,
            inReelCell: false
        }))
    ];

    return jobs.reduce(
        (done, job) =>
            done.then(() => warmUpImage(job.textureId, job.inReelCell)),
        Promise.resolve()
    );
}

function warmUpImage(textureId: string, inReelCell: boolean): Promise<void> {
    const key = `${textureId}|${inReelCell}`;
    const canvas = images.has(key)
        ? null
        : renderTextureCanvas(textureId, inReelCell);
    if (!canvas) {
        return Promise.resolve();
    }
    return new Promise((resolve) =>
        canvas.toBlob((blob) => {
            if (blob && !images.has(key)) {
                images.set(key, URL.createObjectURL(blob));
            }
            resolve();
        }, "image/png")
    );
}

/**
 * The texture from the spritesheets as a URL for an image, or null when it isn't loaded (the name is shown instead
 * and the texture is looked up again next time). Rendered once per texture and cached for the session.
 */
function getTextureImage(
    textureId: string,
    inReelCell: boolean
): string | null {
    const key = `${textureId}|${inReelCell}`;
    if (images.has(key)) {
        return images.get(key)!;
    }

    const canvas = renderTextureCanvas(textureId, inReelCell);
    if (!canvas) {
        return null;
    }

    const image = canvas.toDataURL("image/png");
    images.set(key, image);
    return image;
}

// The texture drawn on a canvas, or null when it isn't loaded or can't be rendered
function renderTextureCanvas(
    textureId: string,
    inReelCell: boolean
): HTMLCanvasElement | null {
    const texture = App.assets.getTexture(textureId);
    if (!texture) {
        return null;
    }

    let canvas: HTMLCanvasElement | null = null;
    let sprite: Sprite | undefined;
    try {
        sprite = new Sprite(texture);
        const { width, height } = texture.orig;
        const cell = inReelCell ? getSymbolCellSize() : { width, height };
        // Not smaller than the image, so nothing is cut off
        const frame = {
            width: Math.max(cell.width, width),
            height: Math.max(cell.height, height)
        };
        sprite.position.set(
            Math.round((frame.width - width) / 2),
            Math.round((frame.height - height) / 2)
        );
        canvas = renderToCanvas(sprite, frame);
    } catch (e) {
        ConsoleUtil.warn(`[HISTORY] Could not render ${textureId}`, e);
    } finally {
        sprite?.destroy();
    }

    return canvas;
}

function renderToCanvas(
    sprite: Sprite,
    frame: { width: number; height: number }
): HTMLCanvasElement {
    const renderer = App.instance.renderer;
    const texture = RenderTexture.create({
        width: frame.width,
        height: frame.height
    });
    try {
        renderer.render(sprite, { renderTexture: texture, clear: true });
        return renderer.plugins.extract.canvas(texture);
    } finally {
        texture.destroy(true);
    }
}

// The "sym" cell of the Reels layout, falling back to its desktop size
function getSymbolCellSize(): { width: number; height: number } {
    const fallback = { width: 165, height: 175 };
    try {
        const sym = ViewManager.getLayer(DefaultLayers.Reels)
            .getCurrentScene()
            ?.getPositionDefinitions()
            .get("sym");
        return sym && sym.width && sym.height
            ? { width: sym.width, height: sym.height }
            : fallback;
    } catch (e) {
        return fallback;
    }
}
